import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams, Link } from 'react-router-dom';
import { api, parseApiError, type ClanSeat, type ClanTermsAcceptance } from '../lib/api';
import { Card } from './Card';
import { StatusBadge } from './StatusBadge';
import { DiscordMemberPicker } from './DiscordMemberPicker';
import { CreateClanForm } from './CreateClanForm';
import { PageSpinner } from './Spinner';
import { cn } from '../lib/cn';
import { MORPH } from '../lib/motion';
import { resolveSeatBadge, type SeatCounts } from '../lib/seatBadge';
import {
  Users,
  ChevronDown,
  ChevronRight,
  UserPlus,
  ArrowLeftRight,
  Shield,
  Upload,
  Trash2,
  RotateCcw,
  CircleCheck,
  CircleDashed,
} from 'lucide-react';

// Same shortId idiom as OrgMembersPanel/OrgProposalsPanel/OrgClansPanel/
// CompetitionMembersPanel — never render a raw Discord snowflake.
function shortId(discordId: string): string {
  return `…${discordId.slice(-6)}`;
}

// ── Terms/rules acceptance indicator (quiet — no borders/glows) ──────────────
// Two states only (durable acceptance — Lisa, 2026-07-08 amendment): a quiet
// accepted check, or a muted "not accepted" hint. No warning/amber state for
// version drift — an old-version acceptance is still just "accepted", with a
// tiny muted version annotation ("v1", or "v1 · now v3" when the comp's rules
// have moved on) rendered informationally, never as a warning. Status is
// resolved once per comp (batched GET /competitions/:compId/terms-acceptance)
// and passed down as a prop, not fetched per-row — keeps this a plain render,
// no extra hooks per clan card.
function TermsIndicator({ acceptance, currentVersion }: { acceptance?: ClanTermsAcceptance; currentVersion?: number }) {
  if (acceptance?.status === 'accepted') {
    const acceptedVersion = acceptance.rulesVersion;
    const versionLabel = acceptedVersion == null
      ? undefined
      : (currentVersion != null && acceptedVersion !== currentVersion
        ? `v${acceptedVersion} · now v${currentVersion}`
        : `v${acceptedVersion}`);
    return (
      <span className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-emerald-400" title="Rules accepted">
        <CircleCheck className="h-3 w-3" /> Terms accepted
        {versionLabel && <span className="normal-case tracking-normal text-ink-meta">{versionLabel}</span>}
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-ink-meta" title="This clan has not accepted the competition's terms yet">
      <CircleDashed className="h-3 w-3" /> Terms not accepted
    </span>
  );
}

function ErrorMsg({ msg }: { msg: string }) {
  return msg ? <p className="mt-2 text-xs text-red-400">{msg}</p> : null;
}

function SuccessMsg({ msg }: { msg: string }) {
  return msg ? <p className="mt-2 text-xs text-green-400">{msg}</p> : null;
}

// ── Seat Management Row ──────────────────────────────────────────────────────

function SeatRow({
  seat,
  clanName,
  isFounder,
  onRefresh,
}: {
  seat: ClanSeat;
  clanName: string;
  isFounder: boolean;
  onRefresh: () => void;
}) {
  const [claimId, setClaimId] = useState('');
  const [swapId, setSwapId] = useState('');
  const [showClaim, setShowClaim] = useState(false);
  const [showSwap, setShowSwap] = useState(false);
  const [error, setError] = useState('');

  const claimMut = useMutation({
    mutationFn: () => api.admin.forceClaimSeat(clanName, seat.gamertag, claimId),
    onSuccess: () => { setShowClaim(false); setClaimId(''); onRefresh(); },
    onError: (e: Error) => setError(parseApiError(e).message),
  });

  const swapMut = useMutation({
    mutationFn: () => api.admin.swapSeat(clanName, seat.gamertag, swapId),
    onSuccess: () => { setShowSwap(false); setSwapId(''); onRefresh(); },
    onError: (e: Error) => setError(parseApiError(e).message),
  });

  return (
    <div className="rounded-lg bg-white/[0.04] p-2.5">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <span className="font-mono text-sm text-white truncate">{seat.gamertag}</span>
          <StatusBadge status={seat.status} />
          {seat.claimedBy && (
            <span className="text-xs text-ink-meta font-mono truncate">{seat.claimedByName ?? shortId(seat.claimedBy)}</span>
          )}
        </div>

        {isFounder && (
          <div className="flex gap-1.5 shrink-0">
            {seat.status === 'GHOST' && (
              <button onClick={() => { setShowClaim(!showClaim); setShowSwap(false); }}
                className={cn('flex items-center gap-1 rounded-md bg-white/[0.05] px-2 py-1 text-[10px] font-bold uppercase text-ink-secondary hover:bg-white/[0.08] hover:text-ink-primary', MORPH)}
                title="Force-claim this seat">
                <Shield className="h-3 w-3" /> Claim
              </button>
            )}
            {seat.status === 'CLAIMED' && (
              <button onClick={() => { setShowSwap(!showSwap); setShowClaim(false); }}
                className={cn('flex items-center gap-1 rounded-md bg-white/[0.05] px-2 py-1 text-[10px] font-bold uppercase text-ink-secondary hover:bg-white/[0.08] hover:text-ink-primary', MORPH)}
                title="Swap seat to another user">
                <ArrowLeftRight className="h-3 w-3" /> Swap
              </button>
            )}
          </div>
        )}
      </div>

      {showClaim && (
        <div className="mt-2 space-y-2">
          <DiscordMemberPicker
            value={claimId}
            onChange={setClaimId}
            search={q => api.searchDiscordMembers(clanName, q)}
            placeholder="Type a name…"
          />
          <button onClick={() => claimMut.mutate()} disabled={!claimId.trim() || claimMut.isPending}
            className="btn-primary text-xs px-3">
            {claimMut.isPending ? '...' : 'Force Claim'}
          </button>
        </div>
      )}

      {showSwap && (
        <div className="mt-2 space-y-2">
          <DiscordMemberPicker
            value={swapId}
            onChange={setSwapId}
            search={q => api.searchDiscordMembers(clanName, q)}
            placeholder="Type a name…"
          />
          <button onClick={() => swapMut.mutate()} disabled={!swapId.trim() || swapMut.isPending}
            className="btn-primary text-xs px-3">
            {swapMut.isPending ? '...' : 'Swap'}
          </button>
        </div>
      )}

      <ErrorMsg msg={error} />
    </div>
  );
}

// ── Expandable Clan Card ─────────────────────────────────────────────────────

function ClanCard({
  clan,
  isFounder,
  isAdminPlus,
  defaultExpanded,
  termsAcceptance,
  termsCurrentVersion,
}: {
  clan: { name: string; status?: string; seatCount?: number; seatCounts?: SeatCounts };
  isFounder: boolean;
  /** Org-admin de-gate (2026-07-17): founder/admin OR an org manager
   *  of this clan's own org (scoped upstream — see EnrolledClans/ClanWorkbench's
   *  own doc comments). Gates Delete — the widened DELETE /clans/:name gate
   *  (isAdminRole OR org owner/admin of the clan's org OR the clan leader). */
  isAdminPlus: boolean;
  defaultExpanded: boolean;
  termsAcceptance?: ClanTermsAcceptance;
  termsCurrentVersion?: number;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const qc = useQueryClient();

  const { data: clanDetail, isLoading } = useQuery({
    queryKey: ['clan', clan.name],
    queryFn: () => api.clan(clan.name),
    enabled: expanded,
  });

  const seats: ClanSeat[] = clanDetail?.seats ?? [];
  const ghostCount = seats.filter(s => s.status === 'GHOST').length;
  const claimedCount = seats.filter(s => s.status === 'CLAIMED').length;
  const pendingCount = seats.filter(s => s.status === 'PENDING_CLAIM').length;
  const refresh = () => qc.invalidateQueries({ queryKey: ['clan', clan.name] });

  // Honest seat badge (2026-07-21): the seat-detail fetch above is
  // enabled-on-expand only, so a collapsed card has no live seats to count
  // — `clanDetail` stays `undefined` until this row is actually expanded
  // and the fetch resolves (an honest empty array still counts as "live").
  // Falls back to the list payload's own seatCounts snapshot, then to an
  // honest "unavailable" placeholder — never a fabricated 0/0/0. See
  // lib/seatBadge.ts's resolveSeatBadge for the fallback order.
  const seatBadge = resolveSeatBadge({
    liveCounts: clanDetail ? { claimed: claimedCount, pending: pendingCount, ghost: ghostCount } : undefined,
    snapshotCounts: clan.seatCounts,
  });

  return (
    <Card className="!p-4">
      <button onClick={() => setExpanded(!expanded)} className="flex w-full items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Users className="h-4 w-4 text-ink-meta shrink-0" />
          <Link to={`/clan/${encodeURIComponent(clan.name)}`}
            onClick={e => e.stopPropagation()}
            className="font-display text-sm font-bold text-white hover:text-accent-500 truncate">
            {clan.name}
          </Link>
          {clan.status && <StatusBadge status={clan.status} />}
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <div className="flex gap-2 text-[10px] font-mono uppercase tracking-wider">
            {seatBadge.counts ? (
              <>
                <span className="text-emerald-400">{seatBadge.counts.claimed} claimed</span>
                <span className="text-ink-secondary">{seatBadge.counts.pending} pending</span>
                <span className="text-ink-meta">{seatBadge.counts.ghost} ghost</span>
              </>
            ) : (
              <span className="text-ink-meta" title="Seat counts unavailable — expand to load">
                counts unavailable
              </span>
            )}
          </div>
          {expanded ? <ChevronDown className="h-4 w-4 text-ink-meta" /> : <ChevronRight className="h-4 w-4 text-ink-meta" />}
        </div>
      </button>

      {termsAcceptance && (
        <div className="mt-2">
          <TermsIndicator acceptance={termsAcceptance} currentVersion={termsCurrentVersion} />
        </div>
      )}

      {expanded && (
        <div className="mt-4 space-y-2">
          {isLoading ? <PageSpinner /> : seats.length === 0 ? (
            <p className="text-xs text-gray-500">No seats yet — add a ghost seat below to get started.</p>
          ) : (
            seats.map(seat => (
              <SeatRow key={seat.gamertag} seat={seat} clanName={clan.name} isFounder={isFounder} onRefresh={refresh} />
            ))
          )}
          <AddSeatInline clanName={clan.name} onAdded={refresh} />
          {isAdminPlus && <DeleteClanButton clanName={clan.name} />}
        </div>
      )}
    </Card>
  );
}

// ── Delete clan (founder, or the owner via their own surface) ────────────────
// SOFT delete — flags the team + seats + comp entries with a 30-day TTL; it's
// hidden but restorable until then. Player profiles/stats are never touched.
function DeleteClanButton({ clanName }: { clanName: string }) {
  const qc = useQueryClient();
  const [error, setError] = useState('');
  const mut = useMutation({
    mutationFn: () => api.deleteClan(clanName),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'clans'] });
      qc.invalidateQueries({ queryKey: ['clan', clanName] });
    },
    onError: (e: Error) => setError(parseApiError(e).message),
  });
  return (
    <div className="mt-3 border-t border-red-500/15 pt-3">
      <button
        onClick={() => {
          if (window.confirm(`Delete the clan "${clanName}"? It's removed from the clan list and kept for 30 days — restore it any time before then from the "Recently deleted" list. Player profiles + stats are never deleted.`)) {
            mut.mutate();
          }
        }}
        disabled={mut.isPending}
        className="inline-flex items-center gap-1.5 rounded-lg border border-red-500/40 bg-red-900/20 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-red-300 hover:bg-red-900/40 disabled:opacity-50"
      >
        <Trash2 className="h-3.5 w-3.5" /> {mut.isPending ? 'Deleting…' : 'Delete clan'}
      </button>
      {error && <p className="mt-1 text-xs text-red-400">{error}</p>}
    </div>
  );
}

// ── Restore a soft-deleted clan within the 30-day window ─────────────────────
function DeletedClanRow({ clanName, expiresAt }: { clanName: string; expiresAt?: number }) {
  const qc = useQueryClient();
  const [error, setError] = useState('');
  const mut = useMutation({
    mutationFn: () => api.restoreClan(clanName),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'clans'] });
      qc.invalidateQueries({ queryKey: ['clan', clanName] });
    },
    onError: (e: Error) => setError(parseApiError(e).message),
  });
  const purges = expiresAt ? new Date(expiresAt * 1000).toLocaleDateString('en-GB') : null;
  return (
    <div className="flex items-center justify-between gap-2 rounded bg-iron-950 px-2.5 py-1.5">
      <div className="min-w-0">
        <span className="truncate font-display text-xs font-bold text-iron-300">{clanName}</span>
        {purges && <span className="ml-2 font-mono text-[9px] uppercase tracking-wider text-amber-400/70">purges {purges}</span>}
        {error && <span className="ml-2 font-mono text-[9px] text-red-400">{error}</span>}
      </div>
      <button
        onClick={() => mut.mutate()}
        disabled={mut.isPending}
        className="inline-flex shrink-0 items-center gap-1 rounded border border-emerald-500/40 bg-emerald-900/20 px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-wider text-emerald-300 hover:bg-emerald-900/40 disabled:opacity-50"
      >
        <RotateCcw className="h-3 w-3" /> {mut.isPending ? 'Restoring…' : 'Restore'}
      </button>
    </div>
  );
}

// ── Add single ghost seat ────────────────────────────────────────────────────

function AddSeatInline({ clanName, onAdded }: { clanName: string; onAdded: () => void }) {
  const [gamertag, setGamertag] = useState('');
  const [claimDiscordId, setClaimDiscordId] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const mut = useMutation({
    mutationFn: ({ gt, did }: { gt: string; did?: string }) => api.createGhostSeat(clanName, gt, did),
    onSuccess: (res) => {
      setGamertag('');
      setClaimDiscordId('');
      setError('');
      setInfo(res.claimDispatched ? 'Seat created + claim DM sent.' : 'Ghost seat created.');
      onAdded();
    },
    // roster_locked (start-date lifecycle gate) and other structured backend
    // errors come back double-JSON-encoded — parseApiError unwraps to the
    // human message instead of showing the raw {"error":...} blob.
    onError: (e: Error) => { setError(parseApiError(e).message); setInfo(''); },
  });

  const submit = () => {
    const trimmed = gamertag.trim();
    if (!trimmed) return;
    const did = claimDiscordId.trim() || undefined;
    mut.mutate({ gt: trimmed, did });
  };

  return (
    <div className="mt-3 space-y-2 pt-1">
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="text"
          placeholder="Gamertag for new ghost seat…"
          value={gamertag}
          onChange={e => setGamertag(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') submit(); }}
          className="input flex-1 min-w-[180px] font-mono text-xs"
        />
        <button
          onClick={submit}
          disabled={mut.isPending || !gamertag.trim()}
          className={cn('inline-flex items-center gap-1 rounded-md bg-white/[0.05] px-2.5 py-1 font-display text-[10px] font-black uppercase tracking-wider text-ink-secondary hover:bg-white/[0.08] hover:text-ink-primary disabled:opacity-50', MORPH)}
        >
          {mut.isPending ? '…' : '+ Add seat'}
        </button>
      </div>
      <DiscordMemberPicker
        value={claimDiscordId}
        onChange={setClaimDiscordId}
        search={q => api.searchDiscordMembers(clanName, q)}
        placeholder="Discord ID (optional, sends DM with claim button)"
      />
      {error && <span className="font-mono text-[10px] text-red-400">{error}</span>}
      {info && !error && <span className="font-mono text-[10px] text-emerald-300">{info}</span>}
    </div>
  );
}

// ── Bulk Import ──────────────────────────────────────────────────────────────

function BulkImportForm({ onDone }: { onDone: () => void }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const mut = useMutation({
    mutationFn: (clans: Array<{ name: string; players: string[] }>) => api.admin.bulkImport(clans),
    onSuccess: (data) => {
      setSuccess(`Created ${data.clansCreated} clans, ${data.seatsCreated} seats${data.errors.length ? ` (${data.errors.length} errors)` : ''}`);
      setText('');
      onDone();
    },
    onError: (e: Error) => setError(parseApiError(e).message),
  });

  const handleSubmit = () => {
    setError('');
    setSuccess('');
    try {
      const parsed = JSON.parse(text);
      if (!Array.isArray(parsed)) throw new Error('Expected an array');
      mut.mutate(parsed);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Invalid JSON');
    }
  };

  if (!open)
    return (
      <button onClick={() => setOpen(true)} className="btn-ghost flex items-center gap-1.5 text-sm">
        <Upload className="h-4 w-4" /> Bulk Import
      </button>
    );

  return (
    <div className="rounded-xl bg-paper-elevated p-4 space-y-3 shadow-[0_1px_2px_0_rgba(0,0,0,0.4)]">
      <p className="text-xs font-semibold uppercase tracking-wider text-ink-meta">Bulk Import Clans + Ghost Seats</p>
      <textarea
        value={text}
        onChange={e => setText(e.target.value)}
        placeholder='[{"name":"Venom Strike","players":["player1_gt","player2_gt"]}]'
        className="input w-full h-32 font-mono text-xs resize-y"
      />
      <p className="text-[10px] text-gray-600">JSON array of {`{ name, players[] }`} — players become ghost seats with those gamertags</p>
      <ErrorMsg msg={error} />
      <SuccessMsg msg={success} />
      <div className="flex gap-2">
        <button onClick={handleSubmit} disabled={!text.trim() || mut.isPending} className="btn-primary flex-1">
          {mut.isPending ? 'Importing...' : 'Import'}
        </button>
        <button onClick={() => { setOpen(false); setError(''); setSuccess(''); }} className="btn-ghost">Cancel</button>
      </div>
    </div>
  );
}

// ── Enroll Existing Clan ─────────────────────────────────────────────────────

function EnrollClanForm({ compId, onDone }: { compId: string; onDone: () => void }) {
  const { data: allClans } = useQuery({ queryKey: ['admin', 'clans'], queryFn: api.admin.clans });
  const { data: enrolled } = useQuery({ queryKey: ['competitionClans', compId], queryFn: () => api.competitionClans(compId), enabled: !!compId });
  const [selected, setSelected] = useState('');
  // M1 of the multi-team epic (Lisa, 2026-07-10): '' = legacy "Main team"
  // (whole-clan) entry — the default, unchanged behavior. A digit string
  // enters that SPECIFIC named team instead.
  const [selectedTeamNo, setSelectedTeamNo] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const enrolledNames = new Set((enrolled ?? []).map(c => c.name));
  const unenrolled = (allClans ?? []).filter(c => !enrolledNames.has(c.name));

  // Only fetched for the CURRENTLY selected clan — /admin/clans's list
  // doesn't carry teamNames, so this is a single lazy lookup, not an N+1
  // across the whole unenrolled list.
  const { data: selectedClanDetail } = useQuery({
    queryKey: ['clanDetailForTeamPick', selected],
    queryFn: () => api.clan(selected),
    enabled: !!selected,
  });
  const teamNames = selectedClanDetail?.teamNames ?? {};
  const teamOptions = Object.entries(teamNames).sort(([a], [b]) => Number(a) - Number(b));

  const mut = useMutation({
    mutationFn: () => api.registerClan(compId, selected, selectedTeamNo ? Number(selectedTeamNo) : undefined),
    onSuccess: () => {
      setSuccess(`Enrolled ${selected}`);
      setSelected('');
      setSelectedTeamNo('');
      onDone();
    },
    onError: (e: Error) => setError(parseApiError(e).message),
  });

  if (unenrolled.length === 0) return null;

  return (
    <div className="rounded-xl bg-paper-elevated p-4 space-y-3 shadow-[0_1px_2px_0_rgba(0,0,0,0.4)]">
      <p className="text-xs font-semibold uppercase tracking-wider text-ink-meta">Enroll Existing Clan</p>
      <div className="flex gap-2">
        <select
          value={selected}
          onChange={e => { setSelected(e.target.value); setSelectedTeamNo(''); setError(''); setSuccess(''); }}
          className="input flex-1 text-sm"
        >
          <option value="">Select a clan...</option>
          {unenrolled.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
        </select>
        <button onClick={() => mut.mutate()} disabled={!selected || mut.isPending}
          className="btn-primary flex items-center gap-1.5 text-sm">
          <UserPlus className="h-4 w-4" /> {mut.isPending ? '...' : 'Enroll'}
        </button>
      </div>
      {teamOptions.length > 0 && (
        <select
          value={selectedTeamNo}
          onChange={e => { setSelectedTeamNo(e.target.value); setError(''); setSuccess(''); }}
          className="input w-full text-sm"
        >
          <option value="">Main team (whole clan)</option>
          {teamOptions.map(([teamNo, teamName]) => (
            <option key={teamNo} value={teamNo}>{teamName}</option>
          ))}
        </select>
      )}
      <ErrorMsg msg={error} />
      <SuccessMsg msg={success} />
    </div>
  );
}

// ── Enrolled clans list (both leader-restricted and admin/founder views) ─────

function EnrolledClans({ compId, paramClan, isFounder, isAdminPlus, restrictToClan }: { compId: string; paramClan: string; isFounder: boolean; isAdminPlus: boolean; restrictToClan?: string }) {
  const compQuery = useQuery({
    queryKey: ['competitionClans', compId],
    queryFn: () => api.competitionClans(compId),
    enabled: !!compId,
  });

  const adminQuery = useQuery({
    queryKey: ['admin', 'clans'],
    queryFn: api.admin.clans,
    enabled: !compId,
  });

  // Terms/rules acceptance — one batched call per comp (admin-setup view
  // only; a leader-restricted view doesn't need the full-comp breakdown).
  // Failures (e.g. a caller who isn't actually in the admin trio) just mean
  // no indicator renders — never blocks the clan list itself.
  const termsQuery = useQuery({
    queryKey: ['compTermsAcceptance', compId],
    queryFn: () => api.getCompTermsAcceptance(compId),
    enabled: !!compId && isAdminPlus,
    retry: false,
  });
  const termsByClanId = new Map((termsQuery.data?.clans ?? []).map(t => [t.clanId, t]));
  const termsCurrentVersion = termsQuery.data?.rulesVersion;

  const isLoading = compId ? compQuery.isLoading : adminQuery.isLoading;
  const rawClans = compId ? compQuery.data : adminQuery.data;

  if (isLoading) return <PageSpinner />;
  if (!rawClans?.length) return <p className="text-sm text-gray-500">No clans found.</p>;

  // Clan display-name preservation (Lisa, 2026-07-13): `c.name` may now be
  // the clan's original display name (e.g. "$kare") rather than the
  // canonical bare-slug form, so a restrictToClan caller that still passes
  // a canonical id (ClanManagement.tsx passes `CLAN#${me.clanName}`) must be
  // compared against `c.clanId` — falling back to the old name-based
  // compare for any caller not yet updated / legacy rows with no clanId.
  const filteredRaw = restrictToClan
    ? rawClans.filter(c => (c as { clanId?: string }).clanId === restrictToClan || c.name === restrictToClan)
    : rawClans;

  if (restrictToClan && filteredRaw.length === 0) {
    return <p className="text-sm text-gray-500">Your clan isn't in this competition.</p>;
  }

  const clans = filteredRaw.map(c => ({
    name: c.name,
    status: 'status' in c ? c.status : undefined,
    seatCount: 'seatCount' in c ? c.seatCount : undefined,
    // Honest seat badges (2026-07-21): only /admin/clans (AdminClan[], the
    // !compId branch) carries this — a comp-scoped CompClan[] row never has
    // it, so ClanCard's badge falls through to its "unavailable" placeholder
    // for that view exactly as it does for any other old/short payload.
    seatCounts: 'seatCounts' in c ? (c as { seatCounts?: SeatCounts }).seatCounts : undefined,
    deletedAt: 'deletedAt' in c ? (c as { deletedAt?: number }).deletedAt : undefined,
    expiresAt: 'expiresAt' in c ? (c as { expiresAt?: number }).expiresAt : undefined,
    // M1 of the multi-team epic (Lisa, 2026-07-10): a team-entry row's
    // `name` is already the qualified "TeamName (ClanName)" display — the
    // terms indicator (per-CLAN, not per-team) needs the real clanId to look
    // itself up correctly; legacy rows have no clanId field and fall back to
    // deriving it from the bare name exactly as before.
    clanId: 'clanId' in c && typeof (c as { clanId?: string }).clanId === 'string' ? (c as { clanId?: string }).clanId : undefined,
  }));
  const activeClans = clans.filter(c => !c.deletedAt);
  const deletedClans = clans.filter(c => c.deletedAt);

  return (
    <>
      {activeClans.map(clan => (
        <ClanCard
          key={clan.name}
          clan={clan}
          isFounder={isFounder}
          isAdminPlus={isAdminPlus}
          defaultExpanded={clan.name === paramClan || clan.clanId === paramClan}
          termsAcceptance={isAdminPlus ? termsByClanId.get(clan.clanId ?? `CLAN#${clan.name}`) : undefined}
          termsCurrentVersion={isAdminPlus ? termsCurrentVersion : undefined}
        />
      ))}
      {/* Org-admin de-gate (2026-07-17): isAdminPlus (founder/admin OR
          an org manager of their own org's clans) instead of isFounder-only —
          matches the widened DELETE/POST-restore gates (isAdminRole OR org
          owner/admin OR the clan leader). */}
      {isAdminPlus && deletedClans.length > 0 && (
        <div className="mt-4 rounded-lg border border-amber-500/20 bg-amber-900/10 p-3">
          <p className="mb-2 font-mono text-[10px] font-bold uppercase tracking-widest text-amber-300">
            Recently deleted · recoverable for 30 days
          </p>
          <div className="space-y-1.5">
            {deletedClans.map(clan => (
              <DeletedClanRow key={clan.name} clanName={clan.name} expiresAt={clan.expiresAt} />
            ))}
          </div>
        </div>
      )}
    </>
  );
}

// ── Composed workbench — the comp-scoped clan-management workbench ───────────
// Renders the EnrolledClans list (for both leaders and admins) plus, when
// isAdminPlus, the right-hand action column (create/enroll/bulk-import).
// paramClan (auto-expand a specific clan card) is read from the URL here so
// callers don't need to thread it through — matches the ?clan= deep-link
// behaviour /admin/clans relied on before this was extracted.
export function ClanWorkbench({
  compId,
  isFounder,
  isAdminPlus,
  restrictToClan,
}: {
  compId: string;
  isFounder: boolean;
  isAdminPlus: boolean;
  restrictToClan?: string;
}) {
  const [searchParams] = useSearchParams();
  const paramClan = searchParams.get('clan') ?? '';
  const qc = useQueryClient();

  const refreshAll = () => {
    qc.invalidateQueries({ queryKey: ['competitionClans'] });
    qc.invalidateQueries({ queryKey: ['admin', 'clans'] });
  };

  return (
    <div className={isAdminPlus ? 'grid gap-6 xl:grid-cols-[1fr_380px]' : ''}>
      {/* Left — Clan list */}
      <div className="space-y-3">
        <EnrolledClans
          compId={compId}
          paramClan={paramClan}
          isFounder={isFounder}
          isAdminPlus={isAdminPlus}
          restrictToClan={restrictToClan}
        />
      </div>

      {/* Right — Actions (admin-only) */}
      {isAdminPlus && (
        <div className="space-y-4">
          <CreateClanForm compId={compId || undefined} onDone={refreshAll} />
          {compId && <EnrollClanForm compId={compId} onDone={refreshAll} />}
          {isFounder && <BulkImportForm onDone={refreshAll} />}
        </div>
      )}
    </div>
  );
}
